import { useState } from 'react';

import { Wrapper } from '@american-autonomy/ag-ui';

import DetailsViewEntityLink from 'components/DetailsViewEntityLink';
import SkeletonText from 'components/loaders/SkeletonText';
import CaptionText from 'components/text/CaptionText';

import useTranslation from './hooks/useTranslation';

import EntityMenu from './components/EntityMenu';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const DetailsViewEntityMenu: FunctionComponent<Props> = ({
    className = '',
    entityType,
    isLoading,
    items,
    onNext,
    testId = TEST_ID,
    totalCount
}) => {
    const {
        APPLICATION,
        BATTERY,
        BOUNDARY,
        CITY,
        CLIENT,
        CONTACT_NAME,
        CONTROLLER_SERIAL_NUMBER,
        DRONE,
        DRONE_SERIAL_NUMBER,
        EMAIL,
        ESTIMATED_ACRES,
        FAA_REGISTRATION_NUMBER,
        FARM,
        FIELD,
        FLIGHT_CONTROLLER_FIRMWARE_VERSION,
        FLIGHT_HOURS,
        NUMBER_OF_FLIGHTS,
        PHONE,
        PILOT,
        PRIMARY_BOUNDARY,
        ROLE,
        SCHEDULED_PILOT,
        SERIAL_NUMBER,
        STATE,
        STATUS,
        TOTAL_ACRES,
        TOTAL_AREA_APPLIED,
        TOTAL_VOLUME_APPLIED,
        USER,
        WARRANTY_END_DATE,
        WARRANTY_REMAINING,
        WARRANTY_START_DATE,
        ZIP
    } = useTranslation();
    const [hasNextPage, setHasNextPage] = useState(!!onNext);

    const labels = {
        application: APPLICATION,
        battery: BATTERY,
        boundary: BOUNDARY,
        city: CITY,
        client: CLIENT,
        contactName: CONTACT_NAME,
        controllerSerialNumber: CONTROLLER_SERIAL_NUMBER,
        drone: DRONE,
        droneSerialNumber: DRONE_SERIAL_NUMBER,
        email: EMAIL,
        estimatedAcres: ESTIMATED_ACRES,
        faaRegistrationNumber: FAA_REGISTRATION_NUMBER,
        farm: FARM,
        field: FIELD,
        flightControllerFirmwareVersion: FLIGHT_CONTROLLER_FIRMWARE_VERSION,
        flightHours: FLIGHT_HOURS,
        numberOfFlights: NUMBER_OF_FLIGHTS,
        phone: PHONE,
        pilot: PILOT,
        primaryBoundary: PRIMARY_BOUNDARY,
        role: ROLE,
        scheduledPilot: SCHEDULED_PILOT,
        serialNumber: SERIAL_NUMBER,
        state: STATE,
        status: STATUS,
        totalAcres: TOTAL_ACRES,
        totalAreaApplied: TOTAL_AREA_APPLIED,
        totalVolumeApplied: TOTAL_VOLUME_APPLIED,
        user: USER,
        warrantyEndDate: WARRANTY_END_DATE,
        warrantyRemaining: WARRANTY_REMAINING,
        warrantyStartDate: WARRANTY_START_DATE,
        zip: ZIP
    };

    const fetchNext = async () => {
        if (!onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const count = totalCount || items?.length || 0;
    const label = labels[entityType];
    const entityTypeLabel = typeof label === 'function' ? label({ count }) : label;

    if (count === 1) {
        const { entityName, entityRoute } = items?.[0] ?? {};

        return (
            <DetailsViewEntityLink
                entityName={entityName ?? ''}
                entityRoute={entityRoute ?? ''}
                entityType={entityType}
            />
        );
    }

    return (
        <Wrapper className={`${className} ${styles.detailsViewEntityMenu}`} testId={testId}>
            <CaptionText className={styles.captionText} text={`${entityTypeLabel}:`} />

            {count === 0 && isLoading ? (
                <SkeletonText lines={1} />
            ) : count > 0 ? (
                <EntityMenu
                    count={count}
                    entityTypeLabel={entityTypeLabel}
                    fetchNext={fetchNext}
                    hasNextPage={hasNextPage}
                    items={items}
                />
            ) : null}
        </Wrapper>
    );
};

export default DetailsViewEntityMenu;
